import React, { useState, useEffect, useRef } from 'react';
import MirrorLoader from './ui/MirrorLoader';
import './../styles/NotificationPage.css';

export default function NotificationPage() {
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const iframeRef                     = useRef(null);
  const clickHandlerRef               = useRef(null); // to clean up later

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/html/notification.html`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(html => setHtmlContent(html))
      .catch(() => setError('Failed to load notification content.'))
      .finally(() => setLoading(false));
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc =
      iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    // Clean existing handler if we re-load
    if (clickHandlerRef.current) {
      doc.removeEventListener('click', clickHandlerRef.current);
    }

    const handleClick = (e) => {
      const target = e.target;

      // Walk up the DOM to find an <a> if they clicked on a child element
      let link = target;
      while (link && link !== doc && link.tagName !== 'A') {
        link = link.parentNode;
      }

      if (!link || link.tagName !== 'A') return;

      const href = link.getAttribute('href');
      if (!href) return;

      // Only intercept PDFs
      if (href.toLowerCase().endsWith('.pdf')) {
        e.preventDefault();

        // Important: navigation is done by the *parent* window,
        // so the iframe sandbox doesn’t apply here.
        window.top.location.href = href;
      }
    };

    clickHandlerRef.current = handleClick;
    doc.addEventListener('click', handleClick);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const iframe = iframeRef.current;
      const doc =
        iframe?.contentDocument || iframe?.contentWindow?.document;
      if (doc && clickHandlerRef.current) {
        doc.removeEventListener('click', clickHandlerRef.current);
      }
    };
  }, []);

  return (
    <div className="notification-container">
      {loading ? (
        <MirrorLoader width="100%" height="3rem" />
      ) : error ? (
        <div className="notification-error">{error}</div>
      ) : (
        <div className="notification-frame">
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            // this is now only needed if code *inside* the iframe tries to navigate
            sandbox="allow-same-origin allow-scripts allow-top-navigation-by-user-activation allow-downloads"
            title="Notification Content"
            style={{ width: '100%', height: '100%', border: 'none' }}
            onLoad={handleIframeLoad}
          />
        </div>
      )}
    </div>
  );
}
